import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Action } from '../models/action.model';
import { GoalPeriodChangeState } from '../models/goal-period-change-state.model';
import { Goal } from '../models/goal.model';
import { GoalsModel } from '../models/goals.model';
import { GoalText } from '../models/goaltext.model';
import { PagedListResult } from '../models/paged-list-result.model';
import { Search } from '../models/search.model';
import { DataService } from './data.service';
import { StateService } from './state.service';
import { Comment } from '../models/comment.model';
import { GoalPeriod } from '../models/goalperiod.model';
import { Result, ResultModel } from '../models/general/result.model';
import { GoalPeriodStatus } from '../enums/goal-period-status.enum';
import { GoalPage } from '../models/goal-page.model';
import { GoalTypeEnum } from '../enums/goal-type.enum';
import { ReviewPeriodCustomGoal } from '../models/review-period-custom-goal.model';
import { GoalPeriodCustomGoal } from '../models/goal-period-custom-goal.model';
import { GoalPeriodApproval } from '../models/domain/goal-period-approvement.model';
import { IdTranslateKey } from '../models/general/id-translate-key.model';
import { TranslateService } from '@ngx-translate/core';
import { IdName } from '../models/general/id-name.model';
import { GoalPeriodApprovalResult } from '../models/goal-period-approval-result.model';

@Injectable({
    providedIn: 'root',
})
export class GoalService extends DataService {
    constructor(http: HttpClient, stateService: StateService, private translate: TranslateService) {
        super(http, stateService);
    }

    private goalPeriodStatusList: IdTranslateKey[] = [
        { id: GoalPeriodStatus.Editing, translateKey: 'GOAL_PERIOD_STATUS.EDITING' },
        { id: GoalPeriodStatus.SelfAssessment, translateKey: 'GOAL_PERIOD_STATUS.SELF_ASSESSMENT' },
        { id: GoalPeriodStatus.LeaderAssessment, translateKey: 'GOAL_PERIOD_STATUS.LEADER_ASSESSMENT' },
        { id: GoalPeriodStatus.Finished, translateKey: 'GOAL_PERIOD_STATUS.FINISHED' },
        { id: GoalPeriodStatus.Approved, translateKey: 'GOAL_PERIOD_STATUS.APPROVED' }
    ];

    public getGoalStatusList(): IdName[] {
        const items = this.goalPeriodStatusList.map(x => {
            return {id: x.id, name: this.translate.instant(x.translateKey)} as IdName;
        });

        return items;
    }

    public getGoalStatusText(status: GoalPeriodStatus): string {
        const item = this.goalPeriodStatusList.find(x => x.id === status as number);
        if (item) {
            return this.translate.instant(item.translateKey);
        }
        return this.translate.instant(this.goalPeriodStatusList[0].translateKey);
    }
    
    getGoals(reviewPeriodId: number, employeeId: number): Observable<GoalsModel>  {
        return this.get(`/api/goals/${reviewPeriodId}/${employeeId}`);
    }

    getGoalTexts = (): Observable<GoalText[]> => {
        return this.get<GoalText[]>('/api/goals/goal-text-list');
    }

    getGoalTextsPaged = (options?: Search): Observable<PagedListResult<GoalText>> => {
        return this.post<PagedListResult<GoalText>>('/api/goals/goal-text-list', options, {headers: {'X-Skip-Interceptor':'1'}});
    }

    changeGoalPeriodState(goalPeriodId: number, status: GoalPeriodStatus): Observable<ResultModel> {
        const data = {goalPeriodId, status} as GoalPeriodChangeState;
        return this.post<ResultModel>('/api/goals/change-goal-period-state/', data);
    }
    
    saveGoal(goal: Goal): Observable<ResultModel> {
        return this.post('/api/goals/goal', goal);
    }
    
    insertAction(goalId: number, action: Action): Observable<Action> {
        return this.put('/api/goals/action/' + goalId.toString(), action);
    }
    
    updateAction(action: Action): Observable<any> {
        return this.post('/api/goals/action', action);
    }

    deleteGoal(id: number): Observable<ResultModel> {
        return this.delete('/api/goals/goal/' + id);
    }

    deleteAction(id): Observable<any> {
        return this.delete('/api/goals/action/' + id);
    }

    getComments(goalPeriodId: number): Observable<Comment[]> {
        return this.get(`/api/goals/list-goalperiod-comments/${goalPeriodId}`);
    }

    getGoalPeriodSettings(goalPeriodId: number): Observable<Result<GoalPeriod>> {
        return this.get(`/api/goals/goal-period-settings/${goalPeriodId}`);
    }

    updateGoalPeriodSettings(goalPeriod: GoalPeriod): Observable<ResultModel> {
        return this.post('/api/goals/goal-period-settings', goalPeriod);
    }

    getGoal(goalId?: number): Observable<GoalPage> {
        const url = goalId ? `/api/goals/goal/${goalId}` : '/api/goals/goal';
        return this.get(url);
    }
    
    getNewGoal(goalPeriodId: number, goalType: GoalTypeEnum): Observable<GoalPage> {
        const url = `/api/goals/new-goal/${goalPeriodId}/${goalType}`;
        return this.get(url);
    }

    getReviewPeriodCustomGoalList = (options?: Search): Observable<PagedListResult<ReviewPeriodCustomGoal>> => {
        if (!options) options = {search: ''} as Search;

        return this.post<PagedListResult<ReviewPeriodCustomGoal>>(`/api/goals/review-period-custom-goals`, options, {headers: {'X-Skip-Interceptor':'1'}});
    }

    insertGoalPeriodCustomGoal(customGoal: GoalPeriodCustomGoal): Observable<Result<GoalPeriodCustomGoal>> {
        return this.put('/api/goals/add-goal-period-custom-goal', customGoal, {headers: {'X-Skip-Interceptor':'1'}} );
    }

    deleteReviewPeriodCustomGoal(id: number): Observable<ResultModel> {
        return this.delete(`/api/goals/review-period-custom-goal/${id}`);
    }

    deleteGroupGoal(id: number): Observable<ResultModel> {
        return this.delete(`/api/goals/group-goal/${id}`);
    }

    approveGoalPeriod(item: GoalPeriodApproval): Observable<Result<GoalPeriodApprovalResult>> {
        return this.post(`/api/goals/approve`, item);
    }

    deleteGoalPeriodApproval(id: number): Observable<ResultModel> {
        return this.delete(`/api/goals/delete-approve/${id}`);
    }

    selectPanoramaResult(goalPeriodId: number, panoramaResultId: number): Observable<ResultModel> {
        return this.post('/api/goals/select-panorama-result', { goalPeriodId, panoramaResultId });
    }

    // updateComment(goalPeriodId: number, reviewPeriodId: number, employeeId: number, comment: string): Observable<any> {
    //     const data = { goalPeriodId, reviewPeriodId, employeeId, comment } as IGoalPeriodComment;
    //     return this.post('/api/goals/comment', data);
    // }  
}
